// 모바일용 회사 등록 폼
import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Building2,
  Phone,
  Mail,
  MapPin,
  Users,
  Plus,
  X,
  ChevronDown,
  ChevronUp,
  Factory,
  Search,
} from "lucide-react";

import { listRoles } from "../../api/users/roles";

const PHONE_PREFIXES = [
  "010",
  "011",
  "016",
  "017",
  "018",
  "019",
  "02",
  "031",
  "032",
  "033",
  "041",
  "042",
  "043",
  "044",
  "051",
  "052",
  "053",
  "054",
  "055",
  "061",
  "062",
  "063",
  "064",
  "070",
  "0505",
  "1588",
  "1577",
  "1661",
  "1522",
];

const EMAIL_DOMAINS = [
  "gmail.com",
  "naver.com",
  "daum.net",
  "kakao.com",
  "nate.com",
  "hotmail.com",
  "icloud.com",
  "__custom__",
];

const digitsOnly = (v) => (v || "").replace(/\D/g, "");
function formatRestByPrefix(prefix, restDigitsRaw) {
  const rest = digitsOnly(restDigitsRaw).slice(0, 8);
  if (rest.length <= 3) return rest;
  if (rest.length <= 7) return `${rest.slice(0, 3)}-${rest.slice(3)}`;
  return `${rest.slice(0, 4)}-${rest.slice(4, 8)}`;
}

const EMPTY_PCP = {
  company: "",
  participant_role: "",
  is_lead: false,
  start_date: "",
  end_date: "",
};

const MobileCompanyForm = ({
  newCompany,
  setNewCompany,
  hasFactories,
  setHasFactories,
  factoryRows,
  setFactoryRows,
  onCreate,
  projectPeriod,
  EMPTY_COMPANY,
  EMPTY_FACTORY_ROW,
}) => {
  const { id: projectId } = useParams();

  // PCP state
  const [roles, setRoles] = useState([]);
  const [pcpForm, setPcpForm] = useState({ ...EMPTY_PCP });
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [roleToAdd, setRoleToAdd] = useState("");
  const [roleLeadFlag, setRoleLeadFlag] = useState(false);

  // Company form state
  const [phonePrefix, setPhonePrefix] = useState("010");
  const [phoneRest, setPhoneRest] = useState("");
  const [emailLocal, setEmailLocal] = useState("");
  const [emailDomainSel, setEmailDomainSel] = useState("gmail.com");
  const [emailDomainCustom, setEmailDomainCustom] = useState("");

  // Accordion sections - 기본적으로 접어두고 필요시 열기
  const [showRoleSection, setShowRoleSection] = useState(true);
  const [showInfoSection, setShowInfoSection] = useState(false);
  const [showFactorySection, setShowFactorySection] = useState(false);

  // 주소 검색 모달
  const [showAddressModal, setShowAddressModal] = useState(false);

  const emailDomain =
    emailDomainSel === "__custom__" ? emailDomainCustom.trim() : emailDomainSel;
  const displayedRest = formatRestByPrefix(phonePrefix, phoneRest);

  const onChangeNew = (key) => (e) =>
    setNewCompany((prev) => ({ ...prev, [key]: e.target.value }));

  useEffect(() => {
    const loadOptions = async () => {
      const rRes = await listRoles({ project: projectId, page_size: 1000 });
      setRoles(rRes.data?.results ?? rRes.data ?? []);
    };
    if (projectId) loadOptions();
  }, [projectId]);

  useEffect(() => {
    if (newCompany.id) {
      if (newCompany.email) {
        const [local, domain] = newCompany.email.split("@");
        setEmailLocal(local);
        if (EMAIL_DOMAINS.includes(domain)) {
          setEmailDomainSel(domain);
          setEmailDomainCustom("");
        } else {
          setEmailDomainSel("__custom__");
          setEmailDomainCustom(domain);
        }
      }
      if (newCompany.phone) {
        const [prefix, ...restParts] = newCompany.phone.split("-");
        setPhonePrefix(prefix || "010");
        setPhoneRest(restParts.join("") || "");
      }
    }
  }, [newCompany]);

  // 다음 주소 검색
  const openDaumPostcode = () => {
    if (!window.daum?.Postcode) {
      const script = document.createElement("script");
      script.src =
        "//t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js";
      script.async = true;
      script.onload = () => openPostcodeModal();
      document.body.appendChild(script);
    } else {
      openPostcodeModal();
    }
  };

  const openPostcodeModal = () => {
    new window.daum.Postcode({
      oncomplete: function (data) {
        let addr = "";
        let extraAddr = "";

        if (data.userSelectedType === "R") addr = data.roadAddress;
        else addr = data.jibunAddress;

        if (data.userSelectedType === "R") {
          if (data.bname && /[동|로|가]$/g.test(data.bname))
            extraAddr += data.bname;
          if (data.buildingName && data.apartment === "Y")
            extraAddr += extraAddr
              ? `, ${data.buildingName}`
              : data.buildingName;
          if (extraAddr) extraAddr = ` (${extraAddr})`;
        }

        setNewCompany((prev) => ({
          ...prev,
          address: addr,
          extra_address: extraAddr,
        }));
      },
      width: "100%",
      height: "100%",
    }).open();
  };

  const handleSubmit = () => {
    if (!newCompany.name.trim()) {
      alert("회사명을 입력하세요.");
      return;
    }
    if (!(newCompany.address || "").trim()) {
      alert("주소를 입력하세요.");
      return;
    }
    if (emailLocal && !emailDomain) {
      alert("이메일 도메인을 선택/입력하세요.");
      return;
    }
    if (selectedRoles.length === 0) {
      alert("프로젝트 참여자 역할을 선택하세요.");
      return;
    }

    const payloadCompany = { ...newCompany };
    if (displayedRest) payloadCompany.phone = `${phonePrefix}-${displayedRest}`;
    if (emailLocal && emailDomain)
      payloadCompany.email = `${emailLocal}@${emailDomain}`;

    const payloadFactories = hasFactories
      ? factoryRows.filter((r) => (r.name || "").trim())
      : [];

    const companyWithRoles = {
      ...payloadCompany,
      project: projectId,
      roles: selectedRoles.map((r) => ({
        participant_role: r.id,
        is_lead: r.is_lead,
        start_date: pcpForm.start_date || null,
        end_date: pcpForm.end_date || null,
      })),
    };

    onCreate({
      company: companyWithRoles,
      factories: payloadFactories,
    });
  };

  const addFactoryRow = () =>
    setFactoryRows((prev) => [...prev, { ...EMPTY_FACTORY_ROW }]);

  const removeFactoryRow = (idx) =>
    setFactoryRows((prev) => prev.filter((_, i) => i !== idx));

  const onChangeFactoryRow = (idx, key, value) =>
    setFactoryRows((prev) =>
      prev.map((r, i) => (i === idx ? { ...r, [key]: value } : r))
    );

  return (
    <div className="space-y-3">
      {/* 프로젝트 참여 역할 섹션 */}
      <div className="section-card overflow-hidden">
        <button
          type="button"
          onClick={() => setShowRoleSection(!showRoleSection)}
          className="w-full flex items-center justify-between p-3 bg-gray-50"
        >
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-blue-600" />
            <span className="text-sm font-semibold">프로젝트 참여 역할 *</span>
            {selectedRoles.length > 0 && (
              <span className="bg-blue-100 text-blue-700 text-[10px] px-1.5 py-0.5 rounded">
                {selectedRoles.length}개 선택
              </span>
            )}
          </div>
          {showRoleSection ? (
            <ChevronUp className="w-4 h-4 text-gray-400" />
          ) : (
            <ChevronDown className="w-4 h-4 text-gray-400" />
          )}
        </button>

        {showRoleSection && (
          <div className="p-3 space-y-3 border-t">
            {/* Role Selection */}
            <div className="space-y-2">
              <select
                value={roleToAdd}
                onChange={(e) => setRoleToAdd(e.target.value)}
                className="w-full border rounded-md px-3 py-2.5 text-sm bg-white"
              >
                <option value="">역할 선택</option>
                {roles.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </select>

              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={roleLeadFlag}
                    onChange={(e) => setRoleLeadFlag(e.target.checked)}
                    className="h-4 w-4"
                  />
                  주관사 여부
                </label>

                <button
                  type="button"
                  onClick={() => {
                    if (!roleToAdd) return alert("역할을 선택하세요.");
                    if (selectedRoles.some((r) => r.id === Number(roleToAdd))) {
                      return alert("이미 추가된 역할입니다.");
                    }
                    setSelectedRoles((prev) => [
                      ...prev,
                      {
                        id: Number(roleToAdd),
                        name: roles.find((r) => r.id == roleToAdd)?.name,
                        is_lead: roleLeadFlag,
                      },
                    ]);
                    setRoleToAdd("");
                    setRoleLeadFlag(false);
                  }}
                  className="btn-create-sm text-xs"
                >
                  추가
                </button>
              </div>
            </div>

            {/* Selected Roles */}
            <div className="flex flex-wrap gap-2">
              {selectedRoles.length === 0 && (
                <p className="text-xs text-gray-500">선택한 역할이 없습니다.</p>
              )}
              {selectedRoles.map((role) => (
                <div
                  key={role.id}
                  className="flex items-center gap-1 bg-blue-50 border border-blue-200 px-2 py-1 rounded-md"
                >
                  <span className="text-xs text-blue-700">
                    {role.name} {role.is_lead && "(주관)"}
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      setSelectedRoles((prev) =>
                        prev.filter((r) => r.id !== role.id)
                      )
                    }
                    className="text-blue-400 hover:text-red-500"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>

            {/* Dates */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">
                  시작일
                </label>
                <input
                  type="date"
                  className="w-full border rounded-md px-2 py-2 text-sm"
                  value={pcpForm.start_date || projectPeriod?.start_date || ""}
                  onChange={(e) =>
                    setPcpForm((f) => ({ ...f, start_date: e.target.value }))
                  }
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">
                  종료일
                </label>
                <input
                  type="date"
                  className="w-full border rounded-md px-2 py-2 text-sm"
                  value={pcpForm.end_date || projectPeriod?.end_date || ""}
                  onChange={(e) =>
                    setPcpForm((f) => ({ ...f, end_date: e.target.value }))
                  }
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 회사 정보 섹션 */}
      <div className="section-card overflow-hidden">
        <button
          type="button"
          onClick={() => setShowInfoSection(!showInfoSection)}
          className="w-full flex items-center justify-between p-3 bg-gray-50"
        >
          <div className="flex items-center gap-2">
            <Building2 className="w-4 h-4 text-blue-600" />
            <span className="text-sm font-semibold">회사 정보 *</span>
            {newCompany.name && (
              <span className="text-xs text-gray-500 truncate max-w-[120px]">
                {newCompany.name}
              </span>
            )}
          </div>
          {showInfoSection ? (
            <ChevronUp className="w-4 h-4 text-gray-400" />
          ) : (
            <ChevronDown className="w-4 h-4 text-gray-400" />
          )}
        </button>

        {showInfoSection && (
          <div className="p-3 space-y-3 border-t">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">
                회사명 *
              </label>
              <input
                type="text"
                placeholder="회사명"
                value={newCompany.name}
                onChange={onChangeNew("name")}
                className="w-full border rounded-md px-3 py-2.5 text-sm"
              />
            </div>

            <div>
              <label className="text-xs text-gray-500 mb-1 block">
                사업자등록번호
              </label>
              <input
                type="text"
                placeholder="000-00-00000"
                value={newCompany.registration_no}
                onChange={onChangeNew("registration_no")}
                className="w-full border rounded-md px-3 py-2.5 text-sm"
              />
            </div>

            {/* Phone - 세로 배치 */}
            <div>
              <label className="text-xs text-gray-500 mb-1 block flex items-center gap-1">
                <Phone className="w-3 h-3" />
                전화번호
              </label>
              <div className="flex gap-2">
                <select
                  value={phonePrefix}
                  onChange={(e) => setPhonePrefix(e.target.value)}
                  className="border rounded-md px-2 py-2.5 text-sm bg-white w-20 flex-shrink-0"
                >
                  {PHONE_PREFIXES.slice(0, 10).map((p) => (
                    <option key={p}>{p}</option>
                  ))}
                </select>
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder="번호 입력"
                  value={displayedRest}
                  onChange={(e) => setPhoneRest(digitsOnly(e.target.value))}
                  className="flex-1 border rounded-md px-3 py-2.5 text-sm min-w-0"
                />
              </div>
            </div>

            {/* Email - 세로 배치 */}
            <div>
              <label className="text-xs text-gray-500 mb-1 block flex items-center gap-1">
                <Mail className="w-3 h-3" />
                이메일
              </label>
              <div className="space-y-2">
                <input
                  type="text"
                  placeholder="이메일 아이디"
                  value={emailLocal}
                  onChange={(e) =>
                    setEmailLocal(e.target.value.replace(/\s/g, ""))
                  }
                  className="w-full border rounded-md px-3 py-2.5 text-sm"
                />
                <div className="flex items-center gap-2">
                  <span className="text-gray-500 text-sm">@</span>
                  <select
                    value={emailDomainSel}
                    onChange={(e) => setEmailDomainSel(e.target.value)}
                    className="flex-1 border rounded-md px-2 py-2.5 text-sm bg-white"
                  >
                    {EMAIL_DOMAINS.map((d) => (
                      <option key={d} value={d}>
                        {d === "__custom__" ? "직접 입력" : d}
                      </option>
                    ))}
                  </select>
                </div>
                {emailDomainSel === "__custom__" && (
                  <input
                    type="text"
                    placeholder="example.com"
                    value={emailDomainCustom}
                    onChange={(e) =>
                      setEmailDomainCustom(e.target.value.replace(/\s/g, ""))
                    }
                    className="w-full border rounded-md px-3 py-2.5 text-sm"
                  />
                )}
              </div>
            </div>

            {/* Address - 모바일 최적화 */}
            <div>
              <label className="text-xs text-gray-500 mb-1 block flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                주소 *
              </label>
              <div className="space-y-2">
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="주소 검색을 눌러주세요"
                    value={newCompany.address || ""}
                    onChange={(e) =>
                      setNewCompany((prev) => ({
                        ...prev,
                        address: e.target.value,
                      }))
                    }
                    className="flex-1 border rounded-md px-3 py-2.5 text-sm min-w-0"
                  />
                  <button
                    type="button"
                    onClick={openDaumPostcode}
                    className="btn-search flex-shrink-0 flex items-center gap-1 text-xs px-3"
                  >
                    <Search className="w-3 h-3" />
                    검색
                  </button>
                </div>
                {newCompany.extra_address && (
                  <p className="text-xs text-gray-500">
                    {newCompany.extra_address}
                  </p>
                )}
                <input
                  type="text"
                  placeholder="상세 주소 (층/호 등)"
                  value={newCompany.detail_address}
                  onChange={onChangeNew("detail_address")}
                  className="w-full border rounded-md px-3 py-2.5 text-sm"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 공장 정보 섹션 */}
      {!newCompany.id && (
        <div className="section-card overflow-hidden">
          <button
            type="button"
            onClick={() => {
              setShowFactorySection(!showFactorySection);
            }}
            className="w-full flex items-center justify-between p-3 bg-gray-50"
          >
            <div className="flex items-center gap-2">
              <Factory className="w-4 h-4 text-gray-400" />
              <span className="text-sm font-medium text-gray-600">
                공장 등록 (선택)
              </span>
            </div>
            {showFactorySection ? (
              <ChevronUp className="w-4 h-4 text-gray-400" />
            ) : (
              <ChevronDown className="w-4 h-4 text-gray-400" />
            )}
          </button>

          {showFactorySection && (
            <div className="p-3 space-y-3 border-t">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={hasFactories}
                  onChange={(e) => setHasFactories(e.target.checked)}
                  className="h-4 w-4"
                />
                공장 있음 (함께 등록)
              </label>

              {hasFactories && (
                <div className="space-y-3">
                  {factoryRows.map((r, idx) => (
                    <div
                      key={idx}
                      className="bg-gray-50 rounded-lg p-3 space-y-2"
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-gray-600">
                          공장 {idx + 1}
                        </span>
                        {factoryRows.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeFactoryRow(idx)}
                            className="text-red-500 text-xs"
                          >
                            삭제
                          </button>
                        )}
                      </div>
                      <input
                        type="text"
                        placeholder="공장명 *"
                        value={r.name}
                        onChange={(e) =>
                          onChangeFactoryRow(idx, "name", e.target.value)
                        }
                        className="w-full border rounded-md px-3 py-2 text-sm"
                      />
                      <input
                        type="text"
                        placeholder="주소"
                        value={r.address}
                        onChange={(e) =>
                          onChangeFactoryRow(idx, "address", e.target.value)
                        }
                        className="w-full border rounded-md px-3 py-2 text-sm"
                      />
                      <input
                        type="text"
                        placeholder="상세 주소"
                        value={r.detail_address}
                        onChange={(e) =>
                          onChangeFactoryRow(
                            idx,
                            "detail_address",
                            e.target.value
                          )
                        }
                        className="w-full border rounded-md px-3 py-2 text-sm"
                      />
                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={addFactoryRow}
                    className="btn-basic-sm w-full flex items-center justify-center gap-1 text-xs"
                  >
                    <Plus className="w-3 h-3" />
                    공장 추가
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* 제출 버튼 */}
      <button
        type="button"
        onClick={handleSubmit}
        className="btn-create w-full py-3 text-sm font-medium"
      >
        {newCompany.id ? "저장" : "회사 등록"}
      </button>
    </div>
  );
};

export default MobileCompanyForm;
